import {
	PaymentModeEnum,
	useEconomicPaymentTermsQuery,
	useGetOrganizationApprovalAndMarginQuery,
	useGetOrganizationSettingQuery,
	useGetSystemSettingsQuery,
	useUpdateOrganizationApprovalAndMarginMutation,
	useUpdateOrganizationInvoiceMinimumMutation,
	useUpdateOrganizationPaymentModeMutation,
	useUpdateOrganizationPaymentTermsMutation,
	useUpdateOrganizationStripeCheckoutLimitMutation,
} from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Everything the client settings section does, minus how it looks.
 *
 * ── Why this exists ──
 *
 * `ClientSettingsTab` is v1's settings: the same four queries, five mutations
 * and dirty-tracking, wrapped in v1's layout. v2 needs the behaviour with a
 * different arrangement — grouped rows, a toggle, unit inputs that offer a
 * reset — and v1 is not to be touched while it is the live page.
 *
 * So the logic lives here, and the v2 section is a render. When v1 retires,
 * `ClientSettingsTab` deletes and nothing else moves; until then this is the
 * one place a settings rule changes for v2, rather than the rules being spread
 * through JSX again.
 *
 * ── Dirty tracking ──
 *
 * Dirtiness is tracked per FIELD, and saved per GROUP: `savePayment` fires the
 * payment mutations whose fields the user actually changed, and only those. The
 * five mutations stay five — the section just stopped asking the user to press
 * three separate Save buttons to commit one visit to the Payment card, which is
 * what the old "every mutation gets its own button" arrangement did.
 *
 * Each group also has a `discard`, which re-reads the last server response. The
 * effects below deliberately skip writing over a field the user has touched —
 * a refetch landing mid-edit must not overwrite what they are typing, which is
 * also why discard has to put the server's values back by hand.
 */

export const PAYMENT_MODE_OPTIONS: { value: PaymentModeEnum; label: string }[] = [
	{ value: PaymentModeEnum.ClientChoice, label: 'Client choice' },
	{ value: PaymentModeEnum.InvoiceOnly, label: 'Invoice only' },
	{ value: PaymentModeEnum.StripeOnly, label: 'Stripe only' },
	{ value: PaymentModeEnum.AdminManual, label: 'Admin manual' },
];

export function useClientSettings(organizationId: string) {
	/* ── system defaults, for the "reset" affordance on the two thresholds ── */
	const { data: systemData, loading: systemLoading } = useGetSystemSettingsQuery({
		fetchPolicy: 'no-cache',
	});
	const systemDefaults = systemData?.getSystemSettings;

	/* ── approval & margin ── */
	const {
		data: approvalData,
		loading: approvalLoading,
		refetch: refetchApproval,
	} = useGetOrganizationApprovalAndMarginQuery({
		variables: { organizationId },
		fetchPolicy: 'no-cache',
	});
	const [updateApproval, { loading: updatingApproval }] =
		useUpdateOrganizationApprovalAndMarginMutation();

	const [defaultApproval, setDefaultApproval] = useState(false);
	const [defaultMargin, setDefaultMargin] = useState('');
	const [approvalDirty, setApprovalDirty] = useState(false);

	useEffect(() => {
		const d = approvalData?.getOrganizationApprovalAndMargin;
		if (!d) return;
		setDefaultApproval(d.defaultApproval);
		setDefaultMargin(d.defaultMargin?.toString() ?? '0');
		setApprovalDirty(false);
	}, [approvalData]);

	const saveApproval = async () => {
		try {
			const result = await updateApproval({
				variables: {
					input: {
						organizationId,
						defaultApproval,
						defaultMargin: parseFloat(defaultMargin) || 0,
					},
				},
			});
			if (result.data?.updateOrganizationApprovalAndMargin.success) {
				toast.success('Settings updated');
				setApprovalDirty(false);
				refetchApproval();
			} else {
				toast.error(
					result.data?.updateOrganizationApprovalAndMargin.message || 'Failed to update'
				);
			}
		} catch {
			toast.error('Failed to update settings');
		}
	};

	/* ── payment modes & thresholds ── */
	const {
		data: orgSettingData,
		loading: orgSettingLoading,
		refetch: refetchOrgSetting,
	} = useGetOrganizationSettingQuery({
		variables: { organizationId },
		fetchPolicy: 'no-cache',
	});
	const [updatePaymentMode, { loading: updatingPaymentMode }] =
		useUpdateOrganizationPaymentModeMutation();
	const [updateThreshold, { loading: updatingThreshold }] =
		useUpdateOrganizationStripeCheckoutLimitMutation();
	const [updateInvoiceMin, { loading: updatingInvoiceMin }] =
		useUpdateOrganizationInvoiceMinimumMutation();

	const [productPaymentMode, setProductPaymentMode] = useState<PaymentModeEnum>(
		PaymentModeEnum.ClientChoice
	);
	const [samplePaymentMode, setSamplePaymentMode] = useState<PaymentModeEnum>(
		PaymentModeEnum.ClientChoice
	);
	const [paymentModeDirty, setPaymentModeDirty] = useState(false);

	const [thresholdInput, setThresholdInput] = useState('');
	const [thresholdDirty, setThresholdDirty] = useState(false);
	const [invoiceMinInput, setInvoiceMinInput] = useState('');
	const [invoiceMinDirty, setInvoiceMinDirty] = useState(false);

	const currentThreshold = orgSettingData?.getOrganizationSetting?.stripeCheckoutLimit;
	const currentInvoiceMin = orgSettingData?.getOrganizationSetting?.invoiceMinimumThreshold;

	/* ── payment terms (e-conomic) ── */
	const { data: paymentTermsData, error: paymentTermsError } = useEconomicPaymentTermsQuery({
		fetchPolicy: 'no-cache',
	});
	const [updatePaymentTerms, { loading: updatingPaymentTerms }] =
		useUpdateOrganizationPaymentTermsMutation();
	const [paymentTermsNumber, setPaymentTermsNumber] = useState('');
	const [paymentTermsDirty, setPaymentTermsDirty] = useState(false);

	/* e-conomic being down is not the same as having no terms — the select is
	   disabled with a reason rather than rendered empty. */
	const paymentTermsUnavailable = Boolean(paymentTermsError);
	const paymentTermsOptions =
		paymentTermsData?.economicPaymentTerms?.map((t) => ({
			value: String(t.paymentTermsNumber),
			label: t.daysOfCredit != null ? `${t.name} (${t.daysOfCredit} days)` : t.name,
		})) ?? [];

	useEffect(() => {
		const s = orgSettingData?.getOrganizationSetting;
		if (!s) return;
		setProductPaymentMode(s.productPaymentMode as PaymentModeEnum);
		setSamplePaymentMode(s.samplePaymentMode as PaymentModeEnum);
		setPaymentModeDirty(false);
		setPaymentTermsNumber(String(s.economicPaymentTermsNumber));
		setPaymentTermsDirty(false);
	}, [orgSettingData]);

	useEffect(() => {
		if (!thresholdDirty && currentThreshold !== undefined) {
			setThresholdInput(currentThreshold != null ? String(currentThreshold) : '');
		}
	}, [currentThreshold, thresholdDirty]);

	useEffect(() => {
		if (!invoiceMinDirty && currentInvoiceMin !== undefined) {
			setInvoiceMinInput(currentInvoiceMin != null ? String(currentInvoiceMin) : '');
		}
	}, [currentInvoiceMin, invoiceMinDirty]);

	const savePaymentMode = async (quiet = false) => {
		try {
			const result = await updatePaymentMode({
				variables: {
					input: { organizationId, productPaymentMode, samplePaymentMode },
				},
			});
			if (result.data?.updateOrganizationPaymentMode.success) {
				setPaymentModeDirty(false);
				if (!quiet) {
					toast.success('Payment modes updated');
					refetchOrgSetting();
				}
				return true;
			}
			if (!quiet) toast.error('Failed to update payment modes');
			return false;
		} catch {
			if (!quiet) toast.error('Failed to update payment modes');
			return false;
		}
	};

	const savePaymentTerms = async () => {
		const selected = paymentTermsData?.economicPaymentTerms?.find(
			(t) => String(t.paymentTermsNumber) === paymentTermsNumber
		);
		if (!selected) return;
		try {
			const result = await updatePaymentTerms({
				variables: {
					input: {
						organizationId,
						economicPaymentTermsNumber: selected.paymentTermsNumber,
					},
				},
			});
			if (result.data?.updateOrganizationPaymentTerms.success) {
				toast.success('Payment terms updated');
				setPaymentTermsDirty(false);
				refetchOrgSetting();
			} else {
				toast.error('Failed to update payment terms');
			}
		} catch {
			toast.error('Failed to update payment terms');
		}
	};

	const saveThreshold = async (quiet = false) => {
		try {
			await updateThreshold({
				variables: {
					input: {
						organizationId,
						threshold: thresholdInput === '' ? null : Number(thresholdInput),
					},
				},
			});
			setThresholdDirty(false);
			if (!quiet) {
				refetchOrgSetting();
				toast.success(
					thresholdInput === ''
						? 'Checkout limit reset to system default'
						: `Checkout limit set to €${Number(thresholdInput).toLocaleString()}`
				);
			}
			return true;
		} catch {
			if (!quiet) toast.error('Failed to update checkout limit');
			return false;
		}
	};

	const saveInvoiceMin = async (quiet = false) => {
		try {
			await updateInvoiceMin({
				variables: {
					input: {
						organizationId,
						threshold: invoiceMinInput === '' ? null : Number(invoiceMinInput),
					},
				},
			});
			setInvoiceMinDirty(false);
			if (!quiet) {
				refetchOrgSetting();
				toast.success(
					invoiceMinInput === ''
						? 'Invoice minimum reset to system default'
						: `Invoice minimum set to ${Number(invoiceMinInput).toLocaleString()} DKK`
				);
			}
			return true;
		} catch {
			if (!quiet) toast.error('Failed to update invoice minimum');
			return false;
		}
	};

	/* ── the Payment card commits as one ──
	   Three mutations behind one button: each runs only if its own field is
	   dirty, all three run quiet, and the card reports once. Anything else makes
	   the admin press Save three times for one visit to the card, or toasts
	   three times for one press. */
	const paymentDirty = paymentModeDirty || thresholdDirty || invoiceMinDirty;
	const savingPayment = updatingPaymentMode || updatingThreshold || updatingInvoiceMin;

	const savePayment = async () => {
		const results = await Promise.all([
			paymentModeDirty ? savePaymentMode(true) : true,
			thresholdDirty ? saveThreshold(true) : true,
			invoiceMinDirty ? saveInvoiceMin(true) : true,
		]);
		refetchOrgSetting();
		if (results.every(Boolean)) toast.success('Payment settings updated');
		else toast.error('Some payment settings could not be saved');
	};

	/* Discard puts the last server response back by hand — the effects above
	   deliberately refuse to overwrite a touched field, so clearing the dirty
	   flag alone would leave the edit on screen. */
	const discardPayment = () => {
		const setting = orgSettingData?.getOrganizationSetting;
		if (setting) {
			setProductPaymentMode(setting.productPaymentMode as PaymentModeEnum);
			setSamplePaymentMode(setting.samplePaymentMode as PaymentModeEnum);
		}
		setThresholdInput(currentThreshold != null ? String(currentThreshold) : '');
		setInvoiceMinInput(currentInvoiceMin != null ? String(currentInvoiceMin) : '');
		setPaymentModeDirty(false);
		setThresholdDirty(false);
		setInvoiceMinDirty(false);
	};

	const discardApproval = () => {
		const d = approvalData?.getOrganizationApprovalAndMargin;
		if (d) {
			setDefaultApproval(d.defaultApproval);
			setDefaultMargin(d.defaultMargin?.toString() ?? '0');
		}
		setApprovalDirty(false);
	};

	const discardPaymentTerms = () => {
		const setting = orgSettingData?.getOrganizationSetting;
		if (setting) setPaymentTermsNumber(String(setting.economicPaymentTermsNumber));
		setPaymentTermsDirty(false);
	};

	return {
		loading: approvalLoading || orgSettingLoading || systemLoading || !systemDefaults,

		/* approval & margin */
		defaultApproval,
		setDefaultApproval: (v: boolean) => {
			setDefaultApproval(v);
			setApprovalDirty(true);
		},
		defaultMargin,
		setDefaultMargin: (v: string) => {
			setDefaultMargin(v);
			setApprovalDirty(true);
		},
		approvalDirty,
		updatingApproval,
		saveApproval,
		discardApproval,

		/* payment modes */
		productPaymentMode,
		setProductPaymentMode: (v: PaymentModeEnum) => {
			setProductPaymentMode(v);
			setPaymentModeDirty(true);
		},
		samplePaymentMode,
		setSamplePaymentMode: (v: PaymentModeEnum) => {
			setSamplePaymentMode(v);
			setPaymentModeDirty(true);
		},
		paymentModeDirty,
		updatingPaymentMode,
		savePaymentMode,

		/* the Payment card as a whole */
		paymentDirty,
		savingPayment,
		savePayment,
		discardPayment,

		/* thresholds — '' means "use the system default" */
		thresholdInput,
		setThresholdInput: (v: string) => {
			setThresholdInput(v);
			setThresholdDirty(true);
		},
		thresholdDirty,
		updatingThreshold,
		saveThreshold,
		defaultStripeLimit: systemDefaults?.defaultStripeCheckoutLimit,

		invoiceMinInput,
		setInvoiceMinInput: (v: string) => {
			setInvoiceMinInput(v);
			setInvoiceMinDirty(true);
		},
		invoiceMinDirty,
		updatingInvoiceMin,
		saveInvoiceMin,
		defaultInvoiceMin: systemDefaults?.defaultInvoiceMinimumThreshold,

		/* payment terms */
		paymentTermsNumber,
		setPaymentTermsNumber: (v: string) => {
			setPaymentTermsNumber(v);
			setPaymentTermsDirty(true);
		},
		paymentTermsDirty,
		paymentTermsOptions,
		paymentTermsUnavailable,
		updatingPaymentTerms,
		savePaymentTerms,
		discardPaymentTerms,
	};
}
